import React, { useState } from "react";
import SEO from "@/components/SEO";
import axios from "axios";
import { toast } from "sonner";
import { PageHero } from "@/components/sections/PageHero";
import { Reveal } from "@/components/effects/Reveal";
import { CONTACT_INFO } from "@/lib/data";
import { Mail, Phone, MessageCircle, MapPin, Clock, Send, Instagram, Facebook, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputBase =
  "w-full bg-black/40 border border-white/10 rounded-md px-4 py-3 text-sm text-white placeholder-white/30 outline-none transition-all font-body focus:border-[#18d3ff] focus:shadow-[0_0_0_3px_rgba(24,211,255,0.10)]";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setDone(true);
      toast.success("Message sent — we’ll get back to you soon.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error("Couldn’t send your message. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-contact">
      <SEO title="Contact — WelkomBiz Photography" description="Get in touch with WelkomBiz Photography for bookings, credits and gallery access." canonicalPath="/contact" />
      <PageHero
        eyebrow="Get in Touch"
        title="Let’s talk about"
        accent="your moment"
        suffix="."
        subtitle="Drop us a line — for bookings, collaborations or general questions. We answer every message personally."
      />

      <section className="relative pb-32">
        <div className="max-w-[1280px] mx-auto px-6 sm:px-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Form */}
          <Reveal className="lg:col-span-7">
            <form data-testid="contact-form" onSubmit={submit} className="glass-strong chrome-border rounded-md p-6 sm:p-10 relative overflow-hidden">
              <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full pointer-events-none"
                style={{ background: "radial-gradient(circle, rgba(24,211,255,0.18), transparent 70%)" }} />
              <div className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full pointer-events-none"
                style={{ background: "radial-gradient(circle, rgba(255,46,147,0.16), transparent 70%)" }} />

              {done && (
                <div className="relative mb-6 flex items-center gap-3 px-4 py-3 rounded-md border border-[#18d3ff]/30 bg-[#18d3ff]/5 text-[#cdf3ff]">
                  <CheckCircle2 className="w-4 h-4" />
                  <span className="text-sm font-body">Message received. Thanks for reaching out.</span>
                </div>
              )}

              <div className="relative grid grid-cols-1 sm:grid-cols-2 gap-5">
                <label className="block">
                  <span className="text-[10px] tracking-luxe uppercase text-white/50 mb-2 block">Name</span>
                  <input data-testid="contact-input-name" required value={form.name} onChange={update("name")} placeholder="Your full name" className={inputBase} />
                </label>
                <label className="block">
                  <span className="text-[10px] tracking-luxe uppercase text-white/50 mb-2 block">Email</span>
                  <input data-testid="contact-input-email" type="email" required value={form.email} onChange={update("email")} placeholder="you@example.com" className={inputBase} />
                </label>
                <label className="block sm:col-span-2">
                  <span className="text-[10px] tracking-luxe uppercase text-white/50 mb-2 block">Phone (optional)</span>
                  <input data-testid="contact-input-phone" value={form.phone} onChange={update("phone")} placeholder="+27 ..." className={inputBase} />
                </label>
                <label className="block sm:col-span-2">
                  <span className="text-[10px] tracking-luxe uppercase text-white/50 mb-2 block">Message</span>
                  <textarea data-testid="contact-input-message" required value={form.message} onChange={update("message")} rows={6} placeholder="How can we help?" className={`${inputBase} resize-none`} />
                </label>
              </div>

              <div className="relative mt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
                <p className="text-[11px] tracking-luxe uppercase text-white/40">
                  We typically reply within a few hours.
                </p>
                <button data-testid="contact-submit" type="submit" disabled={submitting} className="btn-primary disabled:opacity-60">
                  <Send className="w-3.5 h-3.5" />
                  {submitting ? "Sending…" : "Send Message"}
                </button>
              </div>
            </form>
          </Reveal>

          {/* Info */}
          <div className="lg:col-span-5 space-y-4">
            <Reveal>
              <a href={`https://wa.me/${CONTACT_INFO.whatsapp.replace(/\D/g, "")}`} data-testid="contact-whatsapp"
                className="block glass chrome-border rounded-md p-6 lift">
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-full chrome-border bg-black/40 flex items-center justify-center shrink-0">
                    <MessageCircle className="w-4 h-4 text-[#18d3ff]" />
                  </div>
                  <div>
                    <p className="text-[10px] tracking-luxe uppercase text-white/40">WhatsApp · Fastest</p>
                    <h3 className="font-display text-2xl text-chrome mt-1">Chat with us</h3>
                    <p className="text-sm text-white/55 font-body mt-1">{CONTACT_INFO.whatsapp}</p>
                  </div>
                </div>
              </a>
            </Reveal>

            <Reveal delay={120}>
              <div className="glass chrome-border rounded-md p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Mail className="w-4 h-4 text-[#ff2e93]" />
                  <p className="text-[10px] tracking-luxe uppercase text-white/40">Email</p>
                </div>
                <a href={`mailto:${CONTACT_INFO.email}`} className="font-display text-2xl text-chrome hover:opacity-80">{CONTACT_INFO.email}</a>
              </div>
            </Reveal>

            <Reveal delay={220}>
              <div className="glass chrome-border rounded-md p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Phone className="w-4 h-4 text-[#18d3ff]" />
                  <p className="text-[10px] tracking-luxe uppercase text-white/40">Phone</p>
                </div>
                <a href={`tel:${CONTACT_INFO.phone.replace(/\s/g, "")}`} className="font-display text-2xl text-chrome hover:opacity-80">{CONTACT_INFO.phone}</a>
              </div>
            </Reveal>

            <Reveal delay={320}>
              <div className="glass chrome-border rounded-md p-6 space-y-4">
                <div className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 text-[#ff2e93] mt-1" />
                  <div>
                    <p className="text-[10px] tracking-luxe uppercase text-white/40 mb-1">Studio</p>
                    <p className="text-sm text-white/70 font-body">{CONTACT_INFO.location}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Clock className="w-4 h-4 text-[#18d3ff] mt-1" />
                  <div>
                    <p className="text-[10px] tracking-luxe uppercase text-white/40 mb-1">Hours</p>
                    <p className="text-sm text-white/70 font-body">{CONTACT_INFO.hours}</p>
                  </div>
                </div>
                <div className="hairline" />
                <div className="flex items-center gap-3">
                  {[Instagram, Facebook].map((Icon, i) => (
                    <a key={i} href="#" className="w-9 h-9 rounded-full glass chrome-border flex items-center justify-center text-white/70 hover:text-white" data-testid={`contact-social-${i}`}>
                      <Icon className="w-4 h-4" />
                    </a>
                  ))}
                  <span className="text-[10px] tracking-luxe uppercase text-white/40 ml-2">Follow the work</span>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
